/**
 * EventsDataProvider - Provides events data for popup windows
 *
 * This component runs in the main window and listens for POPUP_INIT requests
 * from events popups. It uses data from EventsContext (shared with EventList)
 * to respond to popup requests.
 *
 * Following the popup architecture: popups are stateless views, main window owns all data.
 * 
 * NOTE: This provider gets data from EventsContext, which handles all fetching.
 * The EventsContext also pushes live updates to the popup via DATA_UPDATE messages.
 * This provider only handles the initial POPUP_INIT request.
 */
import { useCallback } from 'react';
import {
  usePopupMessages,
  MainWindowActions,
  MessageType,
  PopupMessage,
  PopupInitPayload,
} from './PopupEvents';
import { PopupType } from '@ais-ui/popups';
import { useSettings } from '../Settings/SettingsProvider';
import { useEventsContext } from '../Events/EventsContext';
import type { PopupEventsData } from './PopupEventsPage';

/**
 * Provider component that responds to popup init requests with events data
 * The actual data fetching is handled by EventsContext
 * Renders nothing - just handles popup communication
 */
export const EventsDataProvider: React.FC = () => {
  const { settings } = useSettings();
  const { events, date, isTrailing } = useEventsContext();

  // Listen for popup init requests
  usePopupMessages(
    useCallback((msg: PopupMessage) => {
      if (msg.type !== MessageType.POPUP_INIT) return;

      const payload = msg.payload as PopupInitPayload;
      if (payload.popupType !== PopupType.Events) return;

      // Calculate active vs deactivated counts
      const activeEvents = events.filter((e) => !e.deactivated).length;
      const deactivatedEvents = events.filter((e) => e.deactivated).length;

      // Generate dataKey based on event content, not date range
      // This should match the format used in EventsContext
      const firstEventId = events[0]?.id || '';
      const lastEventId = events[events.length - 1]?.id || '';
      const dataKey = `${events.length}_${activeEvents}_${firstEventId}_${lastEventId}`;

      const fromStr = date?.from?.toISOString() || '';
      const toStr = date?.to?.toISOString() || '';

      const responseData: PopupEventsData = {
        events,
        totalEvents: events.length,
        activeEvents,
        deactivatedEvents,
        dateRange: {
          from: fromStr,
          to: toStr,
        },
        isTrailing,
        settings: {
          tableDensity: settings['ribbonPlugin.settings']?.table_density || 'compact',
        },
        dataKey,
      };

      MainWindowActions.sendInitResponse(payload.popupId, responseData);
    }, [events, date, settings, isTrailing]),
    [events, date, settings, isTrailing]
  );

  // This component renders nothing - it's just a communication handler
  return null;
};
